import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Messages } from 'config';
import { Details } from 'components';
import { mapQuery } from 'utils/query';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth';
import { useListQuery } from 'store/api/inventory';
import { useAlert, useNavigate, handleMutation } from 'hooks';
import { withLtlInvoiceNumber } from 'utils/ltl-invoice-number';
import { useEditMutation, useDetailsQuery } from 'store/api/stock-transfers';
import {
  withEventSplit,
  getEventSplitInitialValues,
} from 'components/stock-transfer-event-split/config';

import { Form } from './form';
import { getEditItemMaxCount } from './edit-item-max-count';

const Edit = () => {
  const authUser = useAppSelector(selectAuthUser);
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccessAlert } = useAlert();
  const [edit, mutation] = useEditMutation();

  const { data, ...props } = useDetailsQuery(id!);
  const { data: sourceStorageProducts } = useListQuery(
    {
      hideDeletedProducts: true,
      id: String(data?.sourceStorage?.id),
    },
    { skip: !data?.sourceStorage?.id }
  );

  const onSuccess = useCallback(() => {
    showSuccessAlert(`Transfer ${Messages.UPDATED_SUCCESSFULLY}`);
    navigate(-1);
  }, []);

  handleMutation({ ...mutation, onSuccess });

  return (
    <Details {...mapQuery(props)}>
      {data && (
        <Form
          onSubmit={(values) =>
            edit(
              withEventSplit(
                withLtlInvoiceNumber(values, data.ltlInvoiceNumber),
                Boolean(data.eventAllocations?.length)
              )
            )
          }
          initialValues={{
            ...data,
            date: new Date(data.date),
            sourceCompanyId: authUser?.company?.id,
            ltlInvoiceNumber: data.ltlInvoiceNumber ?? '',
            companyTransferType: data.sourceStorage?.type,
            // T-L09 (L14): the saved event split as form rows.
            events: getEventSplitInitialValues(data.eventAllocations),
            items: data.items.map((item) => ({
              cases: item.cases,
              price: item.price,
              units: item.units,
              count: String(item.count),
              productId: item.productId,
              totalPrice: item.price * item.count,
              maxCount: getEditItemMaxCount({
                status: data.status,
                savedCount: item.count,
                availableNow: sourceStorageProducts?.find(
                  (product) => product.productId === item.productId
                )?.count,
              }),
            })),
          }}
        />
      )}
    </Details>
  );
};

export default Edit;
